/* src/modules/interviews/cards/FillInterviewForm/index.js */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { keyBy, map, mapValues, range, reject, upperFirst, zipObject } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Card,
  CardActions,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Check,
  Clear,
  Folder,
  ExpandMore,
  Save,
  EventNote,
} from '@material-ui/icons';

import { DisabledPage, PaperHeader, ConfirmationPopup } from '../../../../app/components';
import { UserHeader, Notes, UserContent } from '../../../../app/components/user';
import ViewDocumentsCard from '../../../tutors/cards/ViewDocumentsCard';
import styles from './styles';

import {
  saveAnswersAndRatings,
  updateApplicantStatus,
  clearApplicantFields,
  fetchInterviews,
  fetchApplicant,
} from '../../actions';
import {
  addNote,
  removeNote,
} from '../../../tutors/actions';
import {
  getAppliedCourses,
  getApplicantQuestions,
  getRatings,
  getApplicantID,
  getNotesList,
} from '../../selectors';

const categories = ['knowledge', 'helpfulness', 'organization'];

class FillInterviewForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      mode: 'reject',
      expanded: false,
      openNotes: false,
      reason: '',
      ...zipObject(categories, categories.map(() => 1)),
      openDocuments: false,
    };

    this._onToggleExpand = this._onToggleExpand.bind(this);
    this._onChangeText = this._onChangeText.bind(this);
    this._onSaveAnswers = this._onSaveAnswers.bind(this);
    this._onOpenApprove = this._onOpenApprove.bind(this);
    this._onOpenReject = this._onOpenReject.bind(this);
    this._onCloseResponse = this._onCloseResponse.bind(this);
    this._onApproveApplicant = this._onApproveApplicant.bind(this);
    this._onRejectApplicant = this._onRejectApplicant.bind(this);
    this._toggleNotes = this._toggleNotes.bind(this);
    this._handleRating = this._handleRating.bind(this);
    this._onChangeReasonText = this._onChangeReasonText.bind(this);
    this._onClickDocs = this._onClickDocs.bind(this);
    this._onCloseDocuments = this._onCloseDocuments.bind(this);
    this.renderRejectContent = this.renderRejectContent.bind(this);

    this._onAddNote = this._onAddNote.bind(this);
    this._onRemoveNote = this._onRemoveNote.bind(this);

    this.popupProps = name => ({
      approve: {
        title: 'Approve this Applicant',
        subtitle: `Are you sure you want to approve ${name} as a tutor?`,
        affirmativeButtonText: 'Approve',
        onSubmit: this._onApproveApplicant,
      },
      reject: {
        title: `Reject ${name}`,
        subtitle: 'Please give the reason for your rejection: ',
        affirmativeButtonText: 'Reject',
        content: this.renderRejectContent,
        onSubmit: this._onRejectApplicant,
      },
    });
  }

  componentWillReceiveProps(nextProps) {
    const keyedQuestions = keyBy(nextProps.questions, '_id');
    this.setState({
      ...mapValues(keyedQuestions, ({ answer }) => answer),
      ...nextProps.ratings,
    });
  }

  _onToggleExpand() {
    this.setState({ expanded: !this.state.expanded });
  }

  _onChangeText(_id) {
    return event => {
      this.setState({ [_id]: event.target.value });
    };
  }

  _onSaveAnswers() {
    const { questions } = this.props;
    const { knowledge, helpfulness, organization } = this.state;
    this.props.saveAnswersAndRatings({
      applicant: this.props.applicantID,
      input: reject(map(questions, ({ _id, text }) => ({
        referenceID: _id,
        question: text,
        answer: this.state[_id],
      })), ({ answer }) => answer === ''),
      ratings: { knowledge, helpfulness, organization },
    });
  }

  _onOpenApprove() {
    this.setState({ open: true, mode: 'approve' });
  }

  _onOpenReject() {
    this.setState({ open: true, mode: 'reject' });
  }

  _onCloseResponse() {
    this.setState({ open: false });
  }

  _onCloseDocuments() {
    this.setState({ openDocuments: false });
  }

  _onApproveApplicant() {
    this.props.updateApplicantStatus({
      applicant: this.props.applicantID,
      status: 'APPROVED',
    })
      .then(() => Promise.resolve(this.props.clearApplicantFields()))
      .then(() => this.props.fetchInterviews())
      .then(() => { this._onCloseResponse(); })
      .catch(() => { this._onCloseResponse(); });
  }

  _onRejectApplicant() {
    this.props.updateApplicantStatus({
      applicant: this.props.applicantID,
      status: 'DECLINED',
      reason: this.state.reason,
    })
      .then(() => Promise.resolve(this.props.clearApplicantFields()))
      .then(() => this.props.fetchInterviews())
      .then(() => { this._onCloseResponse(); })
      .catch(() => { this._onCloseResponse(); });
  }

  _toggleNotes(type) {
    return () => this.setState({ openNotes: type });
  }

  _handleRating(event) {
    this.setState({ [event.target.name]: event.target.value });
  }

  _onChangeReasonText(event) {
    this.setState({ reason: event.target.value });
  }

  _onClickDocs() {
    this.setState({ openDocuments: true });
  }

  async _onAddNote(text) {
    await this.props.addNote({ noteInput: {
      _user: this.props.userID,
      body: text,
    } });
    this.props.fetchApplicant({ tutor: this.props.userID });
  }

  async _onRemoveNote(note) {
    await this.props.removeNote({ note: note._id });
    this.props.fetchApplicant({ tutor: this.props.userID });
  }


  renderRejectContent() {
    return (
      <TextField
        label="Reason for rejection"
        fullWidth
        value={this.state.reason}
        onChange={this._onChangeReasonText}
      />
    );
  }

  renderExpandButton(classes, expanded) {
    return (
      <IconButton
        className={classNames(
          classes.expand,
          { [classes.expandOpen]: expanded }
        )}
        aria-expanded={expanded}
        aria-label="Show more"
        onClick={this._onToggleExpand}
      >
        <ExpandMore />
      </IconButton>
    );
  }

  renderQuestions(classes) {
    const { questions } = this.props;
    return map(questions, ({ _id, text }, i) => (
      <div key={_id} className={classes.questionContainer}>
        <Typography>
          {`${i + 1}.) ${text}`}
        </Typography>
        <Input
          placeholder={`Answer to ${i + 1}`}
          fullWidth
          onChange={this._onChangeText(_id)}
          value={this.state[_id] || ''}
        />
      </div>
    ));
  }

  renderRating(classes) {
    return (
      <form className={classes.ratingContainer} autoComplete="off">
        {categories.map(c => (
          <React.Fragment key={c}>
            <FormControl className={classes.formControl}>
              <InputLabel htmlFor={c}>{upperFirst(c)}</InputLabel>
              <Select
                value={this.state[c]}
                onChange={this._handleRating}
                inputProps={{ name: c, id: c }}
              >
                {range(1, 5.5, 0.5).map(i => (
                  <MenuItem key={`${c}-${i}`} value={i}>{i}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </React.Fragment>
        ))}
      </form>
    );
  }

  renderCourses(classes) {
    const { courses } = this.props;
    return (
      <div className={classes.appliedCourses}>
        <Typography variant="body2">Applied for</Typography>
        {map(courses, (v, k) => (
          <div key={k} className={classes.universityContainer}>
            <Typography key={k} className={classes.university}>
              {k}
            </Typography>
            <div className={classes.courses}>
              {map(v, ({ code, title }) => (
                <Tooltip key={code} id="course" title={title} placement="top">
                  <Typography className={classes.text}>
                    {code}
                  </Typography>
                </Tooltip>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { classes, name, email, phone, bio, education, avatar, userID, notes, hearAboutUs } = this.props;
    const { expanded, openNotes, open, mode, openDocuments } = this.state;
    return (
      <React.Fragment>
        <Card className={classes.card}>
          {userID && (
            <React.Fragment>
              <PaperHeader text="Applicant" />
              <UserHeader name={name} email={email} phone={phone} src={avatar} />
              <CardActions classes={{ root: classes.moreInfo }}>
                <Tooltip id="notes" title="Notes">
                  <IconButton aria-label="Notes" onClick={this._toggleNotes(true)}>
                    <EventNote />
                  </IconButton>
                </Tooltip>
                <Tooltip id="documents" title="View Documents">
                  <IconButton aria-label="Documents" onClick={this._onClickDocs}>
                    <Folder />
                  </IconButton>
                </Tooltip>
                {this.renderExpandButton(classes, expanded)}
                <Divider />
              </CardActions>
              <Divider />
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <UserContent bio={bio} education={education} hearAboutUs={hearAboutUs} />
                {this.renderCourses(classes)}
                <Divider />
              </Collapse>
              <div className={classes.formContainer}>
                <Typography paragraph variant="body2">Questions</Typography>
                {this.renderQuestions(classes)}
                <Typography paragraph variant="body2">Rate this tutor</Typography>
                {this.renderRating(classes)}
              </div>
              <Divider />
              <div className={classes.footer}>
                <div className={classes.save}>
                  <Button className={classes.button} onClick={this._onSaveAnswers}>
                    <Save className={classes.icon} />
                    {'Save Answers and Ratings'}
                  </Button>
                </div>
                <div>
                  <Button
                    color="secondary"
                    variant="raised"
                    className={classes.button}
                    onClick={this._onOpenReject}
                  >
                    <Clear className={classes.icon} />
                    {'Reject'}
                  </Button>
                  <Button
                    color="primary"
                    variant="raised"
                    className={classes.button}
                    onClick={this._onOpenApprove}
                  >
                    <Check className={classes.icon} />
                    {'Approve'}
                  </Button>
                </div>
              </div>
            </React.Fragment>
          )}
        </Card>
        <Notes
          open={openNotes}
          onClose={this._toggleNotes(false)}
          notes={notes}
          onAdd={this._onAddNote}
          onRemove={this._onRemoveNote}
        />
        <ConfirmationPopup
          open={open}
          onClose={this._onCloseResponse}
          {...this.popupProps(name)[mode]}
        />
        <ViewDocumentsCard
          open={openDocuments}
          onClose={this._onCloseDocuments}
          tutorID={userID}
        />
      </React.Fragment>
    );
  }
}

FillInterviewForm.propTypes = {
  classes: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  phone: PropTypes.string.isRequired,
  bio: PropTypes.string.isRequired,
  courses: PropTypes.object.isRequired, // TODO: describe shape
  education: PropTypes.array.isRequired,
  avatar: PropTypes.string.isRequired,
  questions: PropTypes.array.isRequired,
  applicantID: PropTypes.string.isRequired,
  saveAnswersAndRatings: PropTypes.func.isRequired,
  updateApplicantStatus: PropTypes.func.isRequired,
  clearApplicantFields: PropTypes.func.isRequired,
  fetchInterviews: PropTypes.func.isRequired,
  ratings: PropTypes.object.isRequired,
  userID: PropTypes.string.isRequired,
};

const mapStateToProps = state => {
  const { interviews: { applicant: { data: applicantData } } } = state;
  let userInformation;
  if (!applicantData) {
    userInformation = {
      name: 'Full name',
      email: 'Email',
      phone: 'Phone',
      avatar: '',
      education: [],
      bio: 'Bio',
      userID: '',
    };
  } else {
    const {
      user: { first, last, email, phone, avatar, education, howDidYouHear },
      _id,
      bio,
    } = applicantData;
    userInformation = {
      name: `${first} ${last}`,
      email,
      phone,
      avatar,
      education,
      bio,
      userID: _id,
      hearAboutUs: howDidYouHear,
    };
  }

  return {
    ...userInformation,
    courses: getAppliedCourses(state),
    questions: getApplicantQuestions(state),
    applicantID: getApplicantID(state) || '',
    ratings: getRatings(state),
    notes: getNotesList(state),
  };
};

const mapDispatchToProps = dispatch => ({
  saveAnswersAndRatings: params => dispatch(saveAnswersAndRatings(params)()),
  updateApplicantStatus: params => dispatch(updateApplicantStatus(params)()),
  clearApplicantFields: () => dispatch(clearApplicantFields()),
  fetchInterviews: () => dispatch(fetchInterviews()()),
  addNote: params => dispatch(addNote(params)()),
  removeNote: params => dispatch(removeNote(params)()),
  fetchApplicant: params => dispatch(fetchApplicant(params)()),
});

export default compose(
  withStyles(styles, { name: 'FillInterviewForm' }),
  connect(mapStateToProps, mapDispatchToProps)
)(FillInterviewForm);



// WEBPACK FOOTER //
// ./src/modules/interviews/cards/FillInterviewForm/index.js